import { useState, useCallback, useEffect, useRef } from 'react';
import { Exercise } from '@/models/LearnTypes';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';

interface UseSpellingExerciseProps {
  exercises: Exercise[];
  setExercises: React.Dispatch<React.SetStateAction<Exercise[]>>;
  wordsToReview: string[];
  setWordsToReview: React.Dispatch<React.SetStateAction<string[]>>;
  correctlyAnsweredWords: string[];
  setCorrectlyAnsweredWords: React.Dispatch<React.SetStateAction<string[]>>;
  setShowAnswer: React.Dispatch<React.SetStateAction<boolean>>;
  currentExercise: Exercise | undefined;
  userInput: string;
  setIsInputCorrect: React.Dispatch<React.SetStateAction<boolean | null>>;
}

interface UseSpellingExerciseReturn {
  handleCheckSpellingAnswer: () => boolean;
  isInRecyclingPhase: boolean;
  handleCorrectAnswer: () => void;
  handleIncorrectAnswer: () => void;
}

export const useSpellingExercise = ({
  exercises,
  setExercises,
  wordsToReview,
  setWordsToReview,
  correctlyAnsweredWords,
  setCorrectlyAnsweredWords,
  setShowAnswer,
  currentExercise,
  userInput,
  setIsInputCorrect
}: UseSpellingExerciseProps): UseSpellingExerciseReturn => {
  // Current exercise index for tracking progress
  const currentIndex = currentExercise ? exercises.findIndex(ex => ex.id === currentExercise.id) : -1;
  
  // Use perfect level context for consistent tracking
  const { markLevelImperfect } = usePerfectLevel();
  
  // Simple flag to track if we're in a perfect run
  const [hadMistake, setHadMistake] = useState(false);
  const hasBeenMarkedImperfect = useRef(false);
  
  // No longer need recycling phase - simple placeholder to maintain API compatibility
  const isInRecyclingPhase = false;
  
  // Initialize perfect tracking state
  useEffect(() => {
    console.log('🔄 [SpellingExercise] Initializing perfect tracking state');
    setHadMistake(false);
    hasBeenMarkedImperfect.current = false;
    
    // Reset global window flags
    (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = false;
    
    // Get the current level index if available
    try {
      const pathname = window.location.pathname;
      const match = pathname.match(/\/learn\/[^\/]+\/([0-9]+)/);
      if (match && match[1]) {
        const levelIndex = parseInt(match[1], 10);
        // Reset level-specific and global localStorage flags
        localStorage.setItem('spelling_exercise_had_mistake', 'false');
        localStorage.setItem(`spelling_exercise_had_mistake_level_${levelIndex}`, 'false');
        console.log(`🧹 [SpellingExercise] Reset spelling mistake flags for level ${levelIndex}`);
      }
    } catch (e) {
      console.error('Error resetting spelling exercise flags:', e);
    }
  }, [exercises]); // Reset when exercises change
  
  // Simple log for the current word without recycling references
  if (currentExercise) {
    console.debug(`[SpellingExercise] Processing word: ${currentExercise.word}, index: ${currentIndex}`);
  }
  
  // Handle a correct answer - simplified without recycling logic
  const handleCorrectAnswer = useCallback(() => {
    if (!currentExercise) return;
    
    console.debug(`✅ [SpellingExercise] Correct answer for word: ${currentExercise.word}`);
    
    // Add to correctly answered words for analytics only
    if (!correctlyAnsweredWords.includes(currentExercise.id)) {
      setCorrectlyAnsweredWords(prev => {
        const newList = [...prev, currentExercise.id];
        console.debug(`✅ [SpellingExercise] Added ${currentExercise.word} to correctly answered words (count: ${newList.length})`);
        return newList;
      });
    }
    
    // Show the answer
    setShowAnswer(true);
  }, [currentExercise, correctlyAnsweredWords, wordsToReview, setCorrectlyAnsweredWords, setWordsToReview, setShowAnswer]);
  
  // Handle an incorrect answer - consistent with other exercise types
  const handleIncorrectAnswer = useCallback(() => {
    if (!currentExercise) return;
    
    console.log(`❌ [SpellingExercise] Incorrect answer for word: ${currentExercise.word}`);
    
    // Update local state to mark as imperfect
    setHadMistake(true);
    
    // Track challenger words for analytics
    if (!wordsToReview.includes(currentExercise.id)) {
      setWordsToReview(prev => {
        const newList = [...prev, currentExercise.id];
        console.log(`📊 [SpellingExercise] Added to challenging words list for analytics`);
        return newList;
      });
    }
    
    // Don't mark imperfect multiple times
    if (hasBeenMarkedImperfect.current) {
      console.debug(`📝 [SpellingExercise] Level already marked imperfect, skipping redundant markings`);
      setShowAnswer(true);
      return;
    }
    
    // Get the current level index from the URL
    let currentLevelIndex = null;
    try {
      const pathname = window.location.pathname;
      const match = pathname.match(/\/learn\/[^\/]+\/([0-9]+)/);
      if (match && match[1]) {
        currentLevelIndex = parseInt(match[1], 10);
      }
    } catch (e) {
      console.error('Error getting level index from URL:', e);
    }
    
    // Only proceed if we have a valid level index
    if (currentLevelIndex === null) {
      console.error('❌ [SpellingExercise] Could not determine level index, perfect tracking may be inconsistent');
      setShowAnswer(true);
      return;
    }
    
    console.log(`❌ [SpellingExercise] Marking level ${currentLevelIndex} as imperfect - incorrect spelling for ${currentExercise.word}`);
    
    // COMPREHENSIVE TRIPLE-REDUNDANT IMPERFECT MARKING
    try {
      // 1. Use context API (most reliable and modern approach)
      markLevelImperfect(currentLevelIndex);
      
      // 2. Set global window flags for backward compatibility
      (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = true;
      (window as any).__HAD_RECYCLED_WORDS__ = true; // Legacy flag used in activity logging
      (window as any).__PERFECT_LEVEL_STATUS__ = false;
      
      // 3. Set both global and level-specific localStorage flags
      localStorage.setItem('spelling_exercise_had_mistake', 'true');
      localStorage.setItem(`spelling_exercise_had_mistake_level_${currentLevelIndex}`, 'true');
      localStorage.setItem('perfect_level_status', 'false');
      localStorage.setItem('had_recycled_words', 'true');
      localStorage.setItem(`level_${currentLevelIndex}_perfect`, 'false');
      
      // Mark as already processed to avoid duplicate handling
      hasBeenMarkedImperfect.current = true;
      
      console.log(`🔴 [SpellingExercise] IMPERFECT: Level ${currentLevelIndex} fully marked as imperfect in all storage layers`);
    } catch (e) {
      console.error('Error updating perfect tracking flags:', e);
    }
    
    // Show the answer
    setShowAnswer(true);
  }, [currentExercise, wordsToReview, setWordsToReview, setShowAnswer, markLevelImperfect]);
  
  // Main handler for checking spelling answers
  const handleCheckSpellingAnswer = useCallback((): boolean => {
    if (!currentExercise) return false;
    
    console.debug(`[SpellingExercise] Checking answer for word: ${currentExercise.word}, user input: ${userInput}`);
    
    // Function to normalize text for comparison
    const normalizeText = (text: string): string => {
      // First normalize apostrophes (all three variants: straight and both curly types)
      let normalized = text.replace(/['’]/g, "'");
      // Then convert to lowercase and trim whitespace
      normalized = normalized.toLowerCase().trim();
      return normalized;
    };
    
    // Function to remove special characters from the end of words
    const removeTrailingSpecialChars = (text: string): string => {
      return text.replace(/[.,+?!;:…*"()\[\]{}#%^&\-_=+<>~`|\\\/]+$/g, '');
    };
    
    // Function to remove all special characters for a more lenient comparison
    const removeAllSpecialChars = (text: string): string => {
      return text.replace(/[.,+?!;:…*"()\[\]{}#%^&\-_=+<>~`|\\\/]+/g, '');
    };
    
    // Normalize both inputs
    const normalizedUserInput = normalizeText(userInput);
    const normalizedCorrectWord = normalizeText(currentExercise.word);
    
    // Multiple comparison strategies, from strict to lenient
    
    // 1. Exact match after normalization
    if (normalizedUserInput === normalizedCorrectWord) {
      console.debug(`[SpellingExercise] Exact match after normalization`);
      setIsInputCorrect(true);
      handleCorrectAnswer();
      return true;
    }
    
    // 2. Match after removing trailing special characters
    const userInputNoTrailingSpecials = removeTrailingSpecialChars(normalizedUserInput);
    const correctWordNoTrailingSpecials = removeTrailingSpecialChars(normalizedCorrectWord);
    
    if (userInputNoTrailingSpecials === correctWordNoTrailingSpecials) {
      console.debug(`[SpellingExercise] Match after removing trailing special characters`);
      setIsInputCorrect(true);
      handleCorrectAnswer();
      return true;
    }
    
    // 3. Match after removing all special characters
    // This handles both extra special characters added by user and 
    // special characters in the translation that were omitted
    const userInputNoSpecials = removeAllSpecialChars(normalizedUserInput);
    const correctWordNoSpecials = removeAllSpecialChars(normalizedCorrectWord);
    
    if (userInputNoSpecials === correctWordNoSpecials) {
      console.debug(`[SpellingExercise] Match after removing all special characters`);
      setIsInputCorrect(true);
      handleCorrectAnswer();
      return true;
    }
    
    // If none of the matches succeeded, the answer is incorrect
    console.debug(`[SpellingExercise] Answer is incorrect after all comparisons`);
    setIsInputCorrect(false);
    handleIncorrectAnswer();
    return false;
  }, [currentExercise, userInput, setIsInputCorrect, handleCorrectAnswer, handleIncorrectAnswer]);
  
  // Add a final effect to sync state before component unmounts
  useEffect(() => {
    return () => {
      // On cleanup, ensure the hadMistake state is properly reflected in global state
      console.log(`🧹 [SpellingExercise] Cleanup: Setting final state - hadMistake=${hadMistake}`);
      
      if (hadMistake) {
        // If there were mistakes, ensure all systems reflect this consistently
        (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = true;
        (window as any).__HAD_RECYCLED_WORDS__ = true; // Legacy flag used in activity logging
        (window as any).__PERFECT_LEVEL_STATUS__ = false;
        
        // Update global and specific localStorage flags
        localStorage.setItem('spelling_exercise_had_mistake', 'true');
        localStorage.setItem('perfect_level_status', 'false');
        localStorage.setItem('had_recycled_words', 'true');
        
        // Get the current level index from the URL for level-specific flags
        try {
          const pathname = window.location.pathname;
          const match = pathname.match(/\/learn\/[^\/]+\/([0-9]+)/);
          if (match && match[1]) {
            const levelIndex = parseInt(match[1], 10);
            localStorage.setItem(`spelling_exercise_had_mistake_level_${levelIndex}`, 'true');
            localStorage.setItem(`level_${levelIndex}_perfect`, 'false'); // Use consistent key naming
            console.log(`💾 [SpellingExercise] Cleanup: Confirmed level ${levelIndex} as imperfect in all storage layers`);
          }
        } catch (e) {
          console.error('Error setting final level-specific flags:', e);
        }
      }
    };
  }, [hadMistake]);

  return {
    handleCheckSpellingAnswer,
    isInRecyclingPhase: false, // Always false since we removed recycling
    handleCorrectAnswer,
    handleIncorrectAnswer
  };
};
